<template>
  <div class="tw:bg-white" ref="performanceContainer">
    <ContainerLayout class="tw:py-11 tw:flex tw:flex-col">
      <div
        class="tw:flex tw:flex-col tw:md:flex-row tw:justify-between tw:w-full tw:md:items-center tw:mb-6 tw:md:mb-0"
      >
        <InsightsHeader title="plus_performance_header" subtitle="plus_peformance_subtext" />
        <TimeframeFilter
          v-if="!hasEmptyData && !notEnoughData"
          id="plus-insights-timeframe-filter"
          ariaLabel="plus-insights-timeframe-filter"
          size="big"
          name="plus-insights-timeframe-filter"
          :value="timeframeValue"
          label="plus_performance_timeframe"
          :options="timeframeOptions"
          @select="onSelectTimeframeOption"
        />
      </div>
      <div class="tw:flex tw:flex-col tw:gap-y-6 tw:w-full">
        <div class="tw:flex tw:flex-col tw:gap-y-6">
          <PlatformsBanner class="tw:order-1 tw:md:order-0" />
          <PerformanceDataCards
            v-if="!hasEmptyData && !notEnoughData"
            :cardsData="cardsData"
            class="tw:order-0 tw:md:order-1"
          />
        </div>
        <ReactivationNotification v-if="isPlus && isCancelledSubscription && !hasEmptyData && !notEnoughData" />
        <NoDataNotification v-if="hasEmptyData || notEnoughData" />
      </div>
    </ContainerLayout>
  </div>
</template>
<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

import ContainerLayout from '@/layouts/ContainerLayout.vue'
import InsightsHeader from '@/components/studioPlus/insights/InsightsHeader.vue'
import PlatformsBanner from '@/components/studioPlus/insights/PlatformsBanner.vue'
import PerformanceDataCards from '@/components/studioPlus/insights/PerformanceDataCards.vue'
import ReactivationNotification from '@/components/studioPlus/insights/ReactivationNotification.vue'
import NoDataNotification from '@/components/studioPlus/insights/NoDataNotification.vue'
import TimeframeFilter from '@/components/studioPlus/insights/TimeframeFilter.vue'

import { useStudioPlusCampaign } from '@/components/payment/queries'
import { selectedAccommodationId } from '@/layouts/queries'
import { usePlusInsightsData } from '@/components/studioPlus/queries'

import appAnalytics from '@/utils/tracking'

const BadgeViews = new URL('/src/assets/img/studio-plus-insights/badge-views.svg', import.meta.url)
const BadgeClicks = new URL('/src/assets/img/studio-plus-insights/badge-clicks.svg', import.meta.url)
const WebsiteClicks = new URL('/src/assets/img/studio-plus-insights/website-clicks.svg', import.meta.url)

const { isPlus, isCancelledSubscription } = useStudioPlusCampaign(selectedAccommodationId)

const performanceContainer = ref(null)
const isInViewport = ref(false)
const hasTrackedEmptyState = ref(false)
let observer = null

onMounted(() => {
  observer = new IntersectionObserver(
    ([entry]) => {
      isInViewport.value = entry.isIntersecting

      if (entry.isIntersecting) {
        appAnalytics.track(appAnalytics.events.SP_PLUS_INSIGHTS_PERFORMANCE_VIEWED, {
          item_id: selectedAccommodationId.value,
        })
        if (observer) {
          observer.disconnect()
          observer = null
        }
      }
    },
    { threshold: 0.1 }
  )

  if (performanceContainer.value) {
    observer.observe(performanceContainer.value)
  }
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
})

const timeframeValue = ref({ label: 'last_12_months', value: '12' })
const startDate = computed(() => {
  const today = new Date()
  return today.toISOString().split('T')[0]
})
const endDate = computed(() => {
  const today = new Date()
  const months = parseInt(timeframeValue.value.value)

  const start = new Date(today)
  start.setMonth(start.getMonth() - months)

  return start.toISOString().split('T')[0]
})

const timeframeOptions = computed(() => [
  { label: 'last_6_months', value: '6' },
  { label: 'last_12_months', value: '12' },
])

const onSelectTimeframeOption = option => {
  timeframeValue.value = option
}

const { insightsData, hasEmptyData, notEnoughData, isLoading } = usePlusInsightsData(endDate, startDate)

watch(
  [hasEmptyData, notEnoughData, isLoading],
  ([isEmpty, notEnough, loading]) => {
    if (!loading && (isEmpty || notEnough) && !hasTrackedEmptyState.value) {
      appAnalytics.track(appAnalytics.events.SP_PLUS_INSIGHTS_PERFORMANCE_EMPTY_STATE_VIEWED, {
        item_id: selectedAccommodationId.value,
      })
      hasTrackedEmptyState.value = true
    }
  },
  { immediate: true }
)

const cardsData = computed(() => [
  {
    id: 'badgeViews',
    icon: BadgeViews,
    title: 'plus_badge_views',
    hint: 'badge_views_tooltip',
    value: insightsData.value?.badgeViews,
  },
  {
    id: 'badgeClicks',
    icon: BadgeClicks,
    title: 'plus_clicks_badges',
    hint: 'clicks_badges_tooltip',
    value: insightsData.value?.badgeClicks,
  },
  {
    id: 'websiteClicks',
    icon: WebsiteClicks,
    title: 'plus_clicks_contact_details',
    hint: 'clicks_contact_details_tooltip',
    value: insightsData.value?.websiteClicks,
  },
])
</script>
